import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { finalize, Subscription, take } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { GlobalSearchComponent } from '../components/global-search/global-search.component';
import { CustomToastComponent } from '../components/toast/custom-toast.component';
import { NotificationToastComponent } from '../components/toast/notification-toast.component';
import { ApiError, HttpService } from '../services/http.service';
import { NotificationService } from '../services/notification.service';
import { UserProfileService } from '../services/user-profile.service';
import { AccordionComponent } from '../components/dialog/student-faq/student-faq.component';
import { MatDialog } from '@angular/material/dialog';

interface APIResponse {
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  isActive: boolean | string;
  dateOfBirth: string;
  assignedTeacher: string;
  name: string;
  id: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse | APIResponse[];
}

interface Notification {
  id: string;
createdby: any;
  title: string;
  message: string;
  createdAt: string;
}

interface User {
  name: string;
  role: string;
  avatar?: string;
  email: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, FormsModule, GlobalSearchComponent, NgClass],
})
export class HeaderComponent implements OnChanges {
  @Input() searchValue: string = '';
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() searchChanged = new EventEmitter<string>();

  user: User = {
    name: '',
    role: '',
    avatar: '',
    email: '',
  };

  showDropdown = false;
  showNotifications = false;
  studentView: boolean = false;
  notifications: any = [];

  notificationsItem: Notification[] = [];
  notificationCount = 0;

  showSearchBar: boolean = false;
  private profileImageSub?: Subscription;

  private subs = new Subscription();

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
    private eRef: ElementRef,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private dialog: MatDialog,
  ) {
    const roleMap: { [key: string]: string } = {
      superadmin: 'Super Admin',
      csqadmin: 'CSQ Admin',
      csquser: 'CSQ User',
      teacher: 'Teacher',
      student: 'Student',
    };
    const role = localStorage.getItem('userRole') || '';
    const UserDetailsStr = localStorage.getItem('userDetails') || '';
    let name = '';
    let email = '';
    try {
      const UserDetails = UserDetailsStr ? JSON.parse(UserDetailsStr) : null;
      if (UserDetails) {
        name =
          (UserDetails.firstName || '') + ' ' + (UserDetails.lastName || '');
        email = UserDetails?.email;
      }
    } catch (e) {
      name = '';
    }
    this.user.name = name.trim();
    this.user.email = email.trim();
    this.user.role = roleMap[role] || '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchValue']) {
      this.searchValue = changes['searchValue'].currentValue || '';
    }
  }

  ngOnInit(): void {
    this.checkRouteAndShowSearch();
    this.setupRouteListener();

    const token: any = localStorage.getItem('auth_token');
    if (token) {
      this.notificationService.connect(token);
    }

    this.notificationService.getNotifications();
    this.notificationService.getWebNotify(token);

    this.subs.add(
      this.notificationService.notifications$.subscribe((list) => {
        this.notifications = this.filterDismissed(list);
      }),
    );

    this.subs.add(
      this.notificationService.unreadCount$.subscribe((count) => {
        this.notificationCount = count;
      }),
    );

    this.subs.add(
      this.notificationService.newNotification$.subscribe((notification) => {
        this.showNotificationToast(notification);
      }),
    );

    this.subs.add(
      this.userProfileService.user$.subscribe((user) => {
        if (!user) return;
        this.user.name = user.name;
      }),
    );
  }

  private checkRouteAndShowSearch(): void {
    const currentUrl = this.router.url;
    this.showSearchBar = currentUrl.includes('dashboard');
  }

  private setupRouteListener(): void {
    this.subs.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.checkRouteAndShowSearch();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.profileImageSub?.unsubscribe();
    this.subs.unsubscribe();
  }

  onSearchChange(value: string): void {
    this.searchChanged.emit(value);
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) this.showNotifications = false;
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) this.showDropdown = false;
  }

  editProfile(): void {
    this.showDropdown = false;
    const role = localStorage.getItem('userRole') || '';
    const routeMap: { [key: string]: string } = {
      superadmin: '/dashboard/super-admin/edit',
      csqadmin: '/dashboard/csq-admin/edit',
      teacher: '/dashboard/teacher/edit',
      csquser: '/dashboard/user/edit',
    };
    const route = routeMap[role];
    if (route) this.router.navigate([route]);
  }

  goSetting(): void {
    this.showDropdown = false;
    this.router.navigate(['/settings']);
  }

  getUserInitials(): string {
    if (!this.user.name) {
      return '';
    }

    const nameParts = this.user.name.trim().split(' ');

    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  logout(): void {
    this.notificationService.clearAllNotifications(); 
    this.authService.logout();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
      this.showNotifications = false;
    }
  }

  markNotificationRead(id: string) {
    console.log('Removing id:', id);
    console.log('Notifications:', this.notifications);
    this.notifications = this.notifications.filter((n: any) => n.id !== id);
    this.notificationService.markAsRead(id);
  }

  onRedirect(data: any): void {
    const url = data?.redirectUrl;
    const id = data?.id;
    // this.onReadNotification(id, false);
    if (url && id) {
      this.onReadNotification(id, false);
      this.router.navigateByUrl(url);
      this.showNotifications = false;
    } else if (
      data?.title === 'Welcome!' ||
      data?.title === 'Welcome! Teacher.' ||
      data?.title === 'Welcome! Student.'
    ) {
      this.openFaqDialog();
      this.showNotifications = false;
    }
  }

  removeNotification(notificationId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.httpService
      .get(`notifications/notification/delete/${notificationId}`)
      .subscribe({
        next: () => {
          this.notificationService.refreshNotifications();
        },
        error: (err) => {
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  private getDismissedIds(): string[] {
    try {
      const userId = this.user?.email ?? 'default';
      const key = `dismissed_notifications_${userId}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  }

  private saveDismissedId(id: string): void {
    try {
      const userId = this.user?.email ?? 'default';
      const key = `dismissed_notifications_${userId}`;
      const ids = this.getDismissedIds();
      if (!ids.includes(id)) {
        ids.push(id);
        // Keep only the last 200 dismissed IDs to avoid localStorage bloat
        const trimmed = ids.slice(-200);
        localStorage.setItem(key, JSON.stringify(trimmed));
      }
    } catch {
      // localStorage unavailable — silent fail
    }
  }

  private filterDismissed(notifications: any[]): any[] {
    const dismissed = new Set(this.getDismissedIds());
    return notifications.filter((n: any) => !dismissed.has(n.id));
  }

  openFaqDialog(width: string = '50.5rem'): void {
    const updatedNotifications = this.notifications.map((n: any) => ({
      ...n,
      isRead: true,
    }));
    this.notifications = updatedNotifications;
    this.notificationCount = 0;

    const role = localStorage.getItem('userRole');
    let audienceType: 0 | 1 | 2 = 0;
    if (role === 'teacher') {
      audienceType = 1;
    } else if (role === 'csqadmin' || role === 'csquser') {
      audienceType = 2;
    }

    this.dialog.open(AccordionComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { audienceType },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });
  }

  onReadNotification(id: string, type: boolean): void {
    if (type) {
      const updatedNotifications = this.notifications.map((n: any) => ({
        ...n,
        isRead: true,
      }));
      this.notifications = updatedNotifications;
      this.notificationCount = 0;
    } else {
      const updatedNotifications = this.notifications.map((n: any) =>
        n.id === id ? { ...n, isRead: true } : n,
      );
      this.notifications = updatedNotifications;
      this.notificationCount = updatedNotifications.filter(
        (n: any) => !n.isRead,
      ).length;
    }

    const apiCall = type
      ? this.httpService.get<ApiDataResponse>(
          `notifications/notification/read-all`,
        )
      : this.httpService.get<ApiDataResponse>(
          `notifications/notification/read/${id}`,
        );

    apiCall
      .pipe(
        finalize(() => {
          this.showNotifications = false;
        }),
        take(1),
      )
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.showSuccessToast(response.message);
            if (type) {
              setTimeout(() => {
                this.notificationService.refreshNotifications();
              }, 300);
            } else {
              this.notificationService.markAsRead(id);
            }
            this.notificationService.markAsRead(id);
          }
        },
        error: (err: ApiError) => {
          this.notificationService.refreshNotifications();

          if (err.errors) {
            console.log(err.errors);
            Object.values(err.errors).forEach((item: any) => {
              const msg = Array.isArray(item)
                ? item[0]
                : item || 'Something went wrong. Please try again later.';
              this.showErrorToast(msg);
            });
          } else {
            this.showErrorToast(
              err?.message || 'Something went wrong. Please try again later.',
            );
          }
        },
      });
  }

  formatTimeAgo(timestamp?: string | null): string {
    if (!timestamp || typeof timestamp !== 'string') {
      return '';
    }

    const normalized = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(timestamp)
      ? timestamp
      : timestamp + 'Z';

    const time = new Date(normalized);

    if (isNaN(time.getTime()) || time.getFullYear() < 2000) {
      return '';
    }

    const now = new Date();
    const diffInMs = now.getTime() - time.getTime();

    if (diffInMs < 0) {
      return 'Just now';
    }

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 30) {
      return 'Just now';
    }

    if (diffInMinutes < 60) {
      return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    if (diffInDays === 1) {
      return 'Yesterday';
    }

    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    // Display absolute date in Brisbane time (UTC+10, no DST)
    return time.toLocaleDateString('en-AU', {
      timeZone: 'Australia/Brisbane',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  private showNotificationToast(notification: {
    title: string;
    message?: string;
    redirectUrl?: string;
  }): void {
    this.snackBar.openFromComponent(NotificationToastComponent, {
      data: {
        title: notification.title,
        message: notification.message,
        redirectUrl: notification.redirectUrl,
      },
      duration: 10000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: ['notification-bell-toast'],
    });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
